import {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef
} from 'react';
import type { ReactNode } from 'react';
import { isMobile } from 'react-device-detect';
import { round, max, min, floor, exp, pow, log2 } from 'mathjs';
import toPrettyNumber from '../../../functions/toPrettyNumber';
import styles from './SimpleVersion.module.css';
import DayChart from '../DayChart/DayChart';
import SocialSection from '../../SocialSection/SocialSection';
import SocialPopUp from './SocialPopUp/SocialPopUp';
import {
  SimplifiedChartsTop,
  SimplifiedChartsBottom
} from './SimplifiedCharts/SimplifiedCharts';
import UserScore from './UserScore/UserScore';

import { AllInputDataContext, CalculatedDataContext } from '../../../config';
import type {
  SimplifiedData,
  SimplifiedMetric
} from '../../../config/types/context.types';
import { debounce } from 'underscore';

type Props = {
  version: string;
};

/**
 * The raw simplified metric carries calculation inputs (conversion factors,
 * min/max bounds, etc.) that are not part of the presentation-only
 * {@link SimplifiedMetric} model.
 */
type RawSimpleMetric = SimplifiedMetric & {
  converstionFactor: number;
  minValue: number;
  maxValue: number;
  realityFactor: number;
  displayUnits: string;
};

/** A metric after the educational-view transforms below have run. */
type ComputedSimpleMetric = {
  realsticValue: ReactNode;
  actualValue: string | number;
  percentage: number;
  alertLevel: number;
  img: { src: string; width?: string; height?: string };
};

const SimpleVersion = ({ version }: Props) => {
  const { simplifiedData, optionsData } = useContext(AllInputDataContext);
  const { cost, emissions, security } = simplifiedData as SimplifiedData & {
    cost: RawSimpleMetric;
    emissions: RawSimpleMetric;
    security: RawSimpleMetric;
  };
  const { annualConst, reserveRemaining } = useContext(CalculatedDataContext);
  const [userScorePopup, setUserScorePopup] = useState(false);

  const timeOfPopup = useRef(Date.now() - 240000);
  // `cost`/`emissions`/`security` are seeded then immediately replaced with the
  // computed metrics below; the seed values are never read.
  const simpleDataNew: Record<
    'cost' | 'emissions' | 'security',
    ComputedSimpleMetric
  > = { cost, emissions, security } as unknown as Record<
    'cost' | 'emissions' | 'security',
    ComputedSimpleMetric
  >;

  simpleDataNew.cost = simpleCost();
  simpleDataNew.emissions = simpleEmissons();
  simpleDataNew.security = simpleSecurity();
  const totalAlertLevel = round(
    100 *
      // `pow` is typed as `MathType`; the operands here are always numbers.
      (1 - (pow(simpleDataNew.cost.percentage, 2) as number)) *
      (1 - (pow(simpleDataNew.emissions.percentage, 2) as number)) *
      (1 - (pow(simpleDataNew.security.percentage, 2) as number)),
    2
  );
  const socialScorePopup = useCallback(
    debounce((totalAlertLevel: number) => {
      timeOfPopup.current = Date.now();
      setUserScorePopup(true);
    }, 500),
    []
  );

  useEffect(() => {
    if (
      totalAlertLevel > optionsData.minScoreSocialPopup &&
      timeOfPopup.current + 300000 < Date.now()
    ) {
      socialScorePopup(totalAlertLevel);
    }
  }, [optionsData.minScoreSocialPopup, socialScorePopup, totalAlertLevel]);

  return (
    <div className={styles.simpleViewContainer}>
      <div className={styles.simpleChartContainer}>
        <div className={styles.simpleChartContainerTop}>
          <SimplifiedChartsTop
            name={'cost'}
            simpleData={simpleDataNew.cost}
            guageChartID={0}
            style={{ maxWidth: '25%' }}
          />
          <SimplifiedChartsTop
            name={'emissions'}
            simpleData={simpleDataNew.emissions}
            guageChartID={1}
            style={{ maxWidth: '25%' }}
          />
          <SimplifiedChartsTop
            name={'security'}
            simpleData={simpleDataNew.security}
            guageChartID={2}
            style={{ maxWidth: '25%' }}
          />
        </div>
        <div className={styles.simpleChartContainerBottom}>
          <SimplifiedChartsBottom
            name={'cost'}
            simpleData={simpleDataNew.cost}
            style={{ maxWidth: '25%' }}
          />
          <SimplifiedChartsBottom
            name={'emissions'}
            simpleData={simpleDataNew.emissions}
            style={{ maxWidth: '25%' }}
          />
          <SimplifiedChartsBottom
            name={'security'}
            simpleData={simpleDataNew.security}
            style={{ maxWidth: '25%' }}
          />
        </div>
      </div>
      <div
        className={styles.detailedViewContainer}
        style={isMobile ? { minWidth: '100%' } : {}}
      >
        <div className={styles.socialScore}>
          <SocialSection />
          <UserScore
            userScore={round(totalAlertLevel)}
            optionsData={optionsData}
            setUserScorePopup={setUserScorePopup}
          />
        </div>

        <DayChart dayTypeNumber={optionsData.noOfDayTypes} version={version} />
        <DayChart
          dayTypeNumber={optionsData.noOfDayTypes + 1}
          version={version}
        />
      </div>
      {userScorePopup && (
        <SocialPopUp
          userScore={round(totalAlertLevel)}
          optionsData={optionsData}
          setUserScorePopup={setUserScorePopup}
        />
      )}
    </div>
  );

  function simpleCost(): ComputedSimpleMetric {
    const simpleData = {} as ComputedSimpleMetric;

    const costValue = annualConst.totalAnnualCost! * cost.converstionFactor;

    const calculatePercentage = min(
      1,
      max(
        0,

        (costValue - cost.minValue) / (cost.maxValue - cost.minValue)
      )
    );

    simpleData.realsticValue =
      optionsData.currency +
      toPrettyNumber(round(costValue * cost.realityFactor));

    simpleData.actualValue = round(costValue, 2) + cost.displayUnits;
    simpleData.percentage = calculatePercentage;
    const imgNumber = round(simpleData.percentage * 5);
    simpleData.alertLevel = imgNumber;
    simpleData.img = {
      src: `/images/cost/${imgNumber}.jpg`
    };

    return simpleData;
  }

  function simpleEmissons(): ComputedSimpleMetric {
    const simpleData = {} as ComputedSimpleMetric;

    const emissionsValue =
      annualConst.totalAnnualCarbon! * emissions.converstionFactor;

    simpleData.percentage = min(
      1,
      max(
        0,
        1 + (1 / 6) * log2((max(0, emissionsValue) + 1) / emissions.maxValue)
      )
    );

    const imgNumber = round(simpleData.percentage * 5);

    simpleData.actualValue = round(emissionsValue) + emissions.displayUnits;

    simpleData.realsticValue = (
      <>{toPrettyNumber(round(emissionsValue * 0.25 + 20))}cm</>
    );
    simpleData.alertLevel = imgNumber;
    simpleData.img = {
      src: `/images/emissions/${imgNumber}.jpg`
    };

    return simpleData;
  }
  function simpleSecurity(): ComputedSimpleMetric {
    const simpleData = {} as ComputedSimpleMetric;

    simpleData.percentage = max(
      0,

      1 -
        (reserveRemaining - security.minValue) /
          (security.maxValue - security.minValue)
    );

    const getImageAddress = () => {
      let imgNumber = reserveRemaining + 5;
      if (reserveRemaining >= 10) {
        imgNumber = 16;
      } else if (reserveRemaining >= -5) {
        imgNumber = reserveRemaining + 5;
      } else {
        imgNumber = 0;
      }

      const imgNumberBO = 'blackout' + imgNumber;
      return `/images/security/${imgNumberBO}.png`;
    };

    simpleData.img = { src: getImageAddress(), width: '', height: '200px' };

    simpleData.actualValue =
      max(-100, reserveRemaining) + security.displayUnits;

    simpleData.realsticValue =
      toPrettyNumber(
        max(
          0,
          min(8760, round(exp(2.6 - (13.67 * reserveRemaining) / 100) - 3))
        )
      ) + 'hrs';

    simpleData.percentage = min(1, simpleData.percentage);
    simpleData.alertLevel = floor(simpleData.percentage * 5.9999);
    return simpleData;
  }
};
export default SimpleVersion;
